// Builds the 3D circuit for one layout: track surface with kerbs on corners,
// pit lane with one garage per car (or team), start gantry or bridge,
// billboards, grandstands and floodlights. Cars and their motion live in
// entrypoints/track.ts.
import * as THREE from 'three'
import type { Layout } from './layouts'
import type { Billboard } from './race'
import { addTrackside, strip, textures } from './trackside'

const V3 = THREE.Vector3

export type Sample = { p: THREE.Vector3; t: THREE.Vector3; n: THREE.Vector3 }

export type Garage = {
  index: number
  // Where the car stops in the pit lane, and the garage's front centre.
  box: THREE.Vector3
  front: THREE.Vector3
  facing: THREE.Vector3 // from the lane into the garage
  board: THREE.Mesh
  back: THREE.Mesh
}

export type Circuit = {
  curve: THREE.CatmullRomCurve3
  samples: Sample[]
  pitCurve: THREE.CatmullRomCurve3
  pitEntryU: number
  pitExitU: number
  garages: Garage[]
  floodlights: THREE.PointLight[]
  lamps: THREE.MeshBasicMaterial[]
  setGarageLabel: (index: number, name: string, number: number | string, color: string) => void
  setBillboards: (boards: Billboard[]) => void
  setBanner: (title: string, sub: string) => void
  size: number
}

export const FONT = '"Saira Condensed", "Arial Narrow", sans-serif'

export function bannerTexture(title: string, sub: string | null, bg: string, fg: string, w = 1024, h = 256): THREE.CanvasTexture {
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const x = canvas.getContext('2d')!
  x.fillStyle = bg
  x.fillRect(0, 0, w, h)
  x.fillStyle = fg
  x.textAlign = 'center'
  x.textBaseline = 'middle'
  x.font = `italic 800 ${Math.round(h * 0.46)}px ${FONT}`
  x.fillText(title, w / 2, sub ? h * 0.4 : h / 2, w * 0.92)
  if (sub) {
    x.font = `600 ${Math.round(h * 0.17)}px ${FONT}`
    x.fillText(sub.toUpperCase(), w / 2, h * 0.78, w * 0.9)
  }
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = 4
  return texture
}

function checkerTexture(): THREE.CanvasTexture {
  const canvas = document.createElement('canvas')
  canvas.width = 256
  canvas.height = 32
  const x = canvas.getContext('2d')!
  for (let i = 0; i < 16; i++) for (let j = 0; j < 2; j++) {
    x.fillStyle = (i + j) % 2 ? '#0B0C0E' : '#F3F3F1'
    x.fillRect(i * 16, j * 16, 16, 16)
  }
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  return texture
}

const lambert = (o: THREE.MeshLambertMaterialParameters) => new THREE.MeshLambertMaterial({ side: THREE.DoubleSide, ...o })

function sampleCurve(curve: THREE.Curve<THREE.Vector3>, count: number, closed: boolean): Sample[] {
  const samples: Sample[] = []
  for (let i = 0; i < count; i++) {
    const u = closed ? i / count : i / (count - 1)
    const p = curve.getPointAt(u)
    const t = curve.getTangentAt(u)
    samples.push({ p, t, n: new V3(-t.z, 0, t.x) })
  }
  return samples
}

// A flat strip between two offsets from the centre line. colorAt returns a
// color per segment, or null to skip it (kerbs, dashes).
function ribbon(samples: Sample[], inner: number, outer: number, y: number, closed: boolean, colorAt?: (i: number) => THREE.Color | null) {
  const pos: number[] = []
  const col: number[] = []
  const last = closed ? samples.length : samples.length - 1
  for (let i = 0; i < last; i++) {
    let c: THREE.Color | null = null
    if (colorAt) {
      c = colorAt(i)
      if (!c) continue
    }
    const a = samples[i]
    const b = samples[(i + 1) % samples.length]
    const a1 = a.p.clone().addScaledVector(a.n, inner)
    const a2 = a.p.clone().addScaledVector(a.n, outer)
    const b1 = b.p.clone().addScaledVector(b.n, inner)
    const b2 = b.p.clone().addScaledVector(b.n, outer)
    for (const v of [a1, a2, b2, a1, b2, b1]) {
      pos.push(v.x, y, v.z)
      if (c) col.push(c.r, c.g, c.b)
    }
  }
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
  if (colorAt) geometry.setAttribute('color', new THREE.Float32BufferAttribute(col, 3))
  geometry.computeVertexNormals()
  return geometry
}

function nearestU(samples: Sample[], point: THREE.Vector3): number {
  let best = 0
  let bestDistance = Infinity
  samples.forEach((s, i) => {
    const d = s.p.distanceToSquared(point)
    if (d < bestDistance) {
      bestDistance = d
      best = i
    }
  })
  return best / samples.length
}

export function buildCircuit(scene: THREE.Scene, layout: Layout, garageCount: number): Circuit {
  const S = layout.scale
  const W = layout.width
  const curve = new THREE.CatmullRomCurve3(layout.points.map(([x, z]) => new V3(x * S, 0, z * S)), true, 'centripetal')
  const count = Math.round(curve.getLength() / 1.2)
  const samples = sampleCurve(curve, count, true)

  // Centre the world on the circuit.
  const box = new THREE.Box3().setFromPoints(samples.map((s) => s.p))
  const centre = box.getCenter(new V3())
  const size = box.getSize(new V3()).length()

  const steel = new THREE.MeshLambertMaterial({ color: 0x8a8f96 })
  const dark = new THREE.MeshLambertMaterial({ color: 0x1a1c1f })

  const grass = textures.grass()
  grass.repeat.set((size * 3) / 24, (size * 3) / 24)
  const ground = new THREE.Mesh(new THREE.PlaneGeometry(size * 3, size * 3), new THREE.MeshLambertMaterial({ map: grass }))
  ground.rotation.x = -Math.PI / 2
  ground.position.set(centre.x, 0, centre.z)
  ground.receiveShadow = true
  scene.add(ground)

  const asphalt = new THREE.Mesh(strip(samples, () => [-W / 2, W / 2], 0.05), new THREE.MeshLambertMaterial({ map: textures.asphalt() }))
  asphalt.receiveShadow = true
  scene.add(asphalt)

  // White edge lines everywhere, red/white kerbs on corners only.
  const white = new THREE.Color(0xf3f3f1)
  const red = new THREE.Color(0xe0321b)
  const edge = () => white
  scene.add(new THREE.Mesh(ribbon(samples, W / 2 - 0.5, W / 2 - 0.25, 0.07, true, edge), lambert({ vertexColors: true })))
  scene.add(new THREE.Mesh(ribbon(samples, -W / 2 + 0.25, -W / 2 + 0.5, 0.07, true, edge), lambert({ vertexColors: true })))
  const look = 6
  const curvature = samples.map((s, i) => 1 - s.t.dot(samples[(i + look) % samples.length].t))
  const kerb = (i: number) => (curvature[i] > 0.01 || curvature[(i - look + samples.length) % samples.length] > 0.01 ? (Math.floor(i / 2) % 2 ? red : white) : null)
  scene.add(new THREE.Mesh(ribbon(samples, W / 2, W / 2 + 1.4, 0.08, true, kerb), lambert({ vertexColors: true })))
  scene.add(new THREE.Mesh(ribbon(samples, -W / 2, -W / 2 - 1.4, 0.08, true, kerb), lambert({ vertexColors: true })))

  // Which side is "outside" at a sample (away from the circuit centre).
  const outside = (s: Sample) => (s.p.clone().addScaledVector(s.n, 1).distanceTo(centre) > s.p.distanceTo(centre) ? 1 : -1)
  const at = (u: number) => samples[Math.floor(((u % 1) + 1) % 1 * samples.length)]

  // ── Pit lane ──────────────────────────────────────────────────────────
  const pitFrom = new V3(layout.pitFrom[0] * S, 0, layout.pitFrom[1] * S)
  const pitTo = new V3(layout.pitTo[0] * S, 0, layout.pitTo[1] * S)
  const laneDir = pitTo.clone().sub(pitFrom).normalize()
  const laneNormal = new V3(-laneDir.z, 0, laneDir.x)
  const laneStart = pitFrom.clone().addScaledVector(laneNormal, layout.pitOffset)
  const laneEnd = pitTo.clone().addScaledVector(laneNormal, layout.pitOffset)
  const laneLength = pitFrom.distanceTo(pitTo)
  // The lane peels off the edge of the straight and runs parallel to it: the
  // entry and exit sit on the pit-side half of the track, on the straight,
  // and blend into the lane over a long, gentle taper.
  const entryPoint = pitFrom.clone().addScaledVector(laneDir, -laneLength * layout.pitEntryLead)
  const exitPoint = pitTo.clone().addScaledVector(laneDir, laneLength * layout.pitExitLead)
  const pitEntryU = nearestU(samples, entryPoint)
  const pitExitU = nearestU(samples, exitPoint)
  const pitSide = Math.sign(layout.pitOffset)
  const laneEdge = pitSide * (W / 2 - 2)
  const entryOnTrack = at(pitEntryU).p.clone().addScaledVector(laneNormal, laneEdge * 0.5)
  const exitOnTrack = at(pitExitU).p.clone().addScaledVector(laneNormal, laneEdge * 0.5)
  const entryAlong = entryOnTrack.clone().addScaledVector(laneNormal, -entryOnTrack.clone().sub(pitFrom).dot(laneNormal))
  const exitAlong = exitOnTrack.clone().addScaledVector(laneNormal, -exitOnTrack.clone().sub(pitTo).dot(laneNormal))
  const pitCurve = new THREE.CatmullRomCurve3([
    entryOnTrack,
    entryAlong.clone().lerp(pitFrom, 0.5).addScaledVector(laneNormal, laneEdge + (layout.pitOffset - laneEdge) * 0.35),
    laneStart,
    laneStart.clone().lerp(laneEnd, 0.5),
    laneEnd,
    exitAlong.clone().lerp(pitTo, 0.5).addScaledVector(laneNormal, laneEdge + (layout.pitOffset - laneEdge) * 0.35),
    exitOnTrack,
  ], false, 'centripetal')
  const pitSamples = sampleCurve(pitCurve, 240, false)
  scene.add(new THREE.Mesh(ribbon(pitSamples, -3.2, 3.2, 0.04, false), lambert({ color: 0x34363b })))
  // Lane edge lines only where the lane is off the track surface.
  const onTrack = (point: THREE.Vector3) => samples.some((s) => s.p.distanceToSquared(point) < (W / 2 + 0.6) ** 2)
  const laneLine = (offset: number) => (i: number) => (onTrack(pitSamples[i].p.clone().addScaledVector(pitSamples[i].n, offset)) ? null : white)
  scene.add(new THREE.Mesh(ribbon(pitSamples, -3.4, -3.2, 0.06, false, laneLine(-3.3)), lambert({ vertexColors: true })))
  scene.add(new THREE.Mesh(ribbon(pitSamples, 3.2, 3.4, 0.06, false, laneLine(3.3)), lambert({ vertexColors: true })))

  // Pit wall between the lane and the track.
  {
    const wallMid = pitFrom.clone().lerp(pitTo, 0.5).addScaledVector(laneNormal, layout.pitOffset / 2)
    const wall = new THREE.Mesh(new THREE.BoxGeometry(0.5, 1.1, laneLength), new THREE.MeshLambertMaterial({ color: 0xd8d8d2 }))
    wall.position.set(wallMid.x, 0.55, wallMid.z)
    wall.rotation.y = Math.atan2(laneDir.x, laneDir.z)
    wall.castShadow = true
    scene.add(wall)
  }

  // ── Garages: one per car (Grand Prix) or per team (Endurance) ─────────
  const garages: Garage[] = []
  const side = Math.sign(layout.garageOffset) || 1
  const rowStart = laneStart.clone().addScaledVector(laneDir, laneLength * 0.04 + (layout.garageShift ?? 0))
  const rowLength = laneLength * 0.92
  const pitch = rowLength / Math.max(1, garageCount)
  const angle = Math.atan2(laneDir.x, laneDir.z)
  const building = new THREE.Group()
  {
    const depth = 9
    const mid = rowStart.clone().addScaledVector(laneDir, rowLength / 2).addScaledVector(laneNormal, layout.garageOffset + side * (depth / 2))
    const roof = new THREE.Mesh(new THREE.BoxGeometry(depth + 2, 0.6, rowLength + 2), new THREE.MeshLambertMaterial({ color: 0xe9e9e4 }))
    roof.position.set(mid.x, 5.3, mid.z)
    roof.rotation.y = angle
    roof.castShadow = true
    building.add(roof)
    const backWall = new THREE.Mesh(new THREE.BoxGeometry(0.6, 5, rowLength + 2), dark)
    const backPos = mid.clone().addScaledVector(laneNormal, side * depth / 2)
    backWall.position.set(backPos.x, 2.5, backPos.z)
    backWall.rotation.y = angle
    building.add(backWall)
    // Hospitality floor above the boxes.
    const upper = new THREE.Mesh(new THREE.BoxGeometry(depth + 2, 3, rowLength + 2), new THREE.MeshLambertMaterial({ color: 0x2b2e33 }))
    upper.position.set(mid.x, 7.1, mid.z)
    upper.rotation.y = angle
    upper.castShadow = true
    building.add(upper)
  }
  scene.add(building)

  const crewColors = [0xff5a1f, 0x4c9aff, 0xf3f3f1, 0xffd23f]
  const tyreMat = new THREE.MeshLambertMaterial({ color: 0x0a0a0a })
  for (let g = 0; g < garageCount; g++) {
    const along = rowStart.clone().addScaledVector(laneDir, pitch * (g + 0.5))
    const front = along.clone().addScaledVector(laneNormal, layout.garageOffset)
    const facing = laneNormal.clone().multiplyScalar(side)
    const boxPoint = along.clone().addScaledVector(laneNormal, side * 1.5) // rowStart is already on the lane
    const group = new THREE.Group()
    group.position.copy(front)
    group.rotation.y = Math.atan2(facing.x, facing.z)

    // Garage walls (left/right), colored back panel, light strip.
    for (const x of [-pitch / 2 + 0.15, pitch / 2 - 0.15]) {
      const wall = new THREE.Mesh(new THREE.BoxGeometry(0.3, 5, 9), new THREE.MeshLambertMaterial({ color: 0x3a3e45 }))
      wall.position.set(x, 2.5, 4.5)
      group.add(wall)
    }
    const back = new THREE.Mesh(new THREE.BoxGeometry(pitch - 0.6, 4.6, 0.2), new THREE.MeshLambertMaterial({ color: 0x555555 }))
    back.position.set(0, 2.3, 8.6)
    group.add(back)
    const strip = new THREE.Mesh(new THREE.BoxGeometry(pitch - 0.8, 0.12, 0.6), new THREE.MeshBasicMaterial({ color: 0xfff1d6 }))
    strip.position.set(0, 4.85, 4)
    group.add(strip)
    // Name board above the opening.
    const board = new THREE.Mesh(new THREE.PlaneGeometry(pitch - 0.4, 1.3), new THREE.MeshBasicMaterial({ color: 0x222222 }))
    board.position.set(0, 4.25, -0.05)
    board.rotation.y = Math.PI
    group.add(board)
    // Tyre stacks and crew.
    for (const [x, z] of [[-pitch / 2 + 1, 6.5], [pitch / 2 - 1, 6.5]]) {
      for (let k = 0; k < 4; k++) {
        const tyre = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, 0.32, 12), tyreMat)
        tyre.position.set(x, 0.17 + k * 0.33, z)
        group.add(tyre)
      }
    }
    for (let c = 0; c < 4; c++) {
      const crew = new THREE.Mesh(new THREE.BoxGeometry(0.45, 1.6, 0.35), new THREE.MeshLambertMaterial({ color: crewColors[(g + c) % crewColors.length] }))
      crew.position.set(-pitch / 2 + 1 + c * ((pitch - 2) / 3), 0.8, 1.2 + (c % 2) * 1.4)
      group.add(crew)
    }
    // Box marking on the lane.
    // Box marking on the lane: a thin yellow outline where the car stops.
    const markMaterial = new THREE.MeshBasicMaterial({ color: 0xffd23f })
    const markZ = -(Math.abs(layout.garageOffset) - 1.5)
    for (const [w, d, x, z] of [[pitch - 1.4, 0.15, 0, markZ - 2.6], [pitch - 1.4, 0.15, 0, markZ + 2.6], [0.15, 5.2, -(pitch - 1.4) / 2, markZ], [0.15, 5.2, (pitch - 1.4) / 2, markZ]]) {
      const line = new THREE.Mesh(new THREE.PlaneGeometry(w, d), markMaterial)
      line.rotation.x = -Math.PI / 2
      line.position.set(x, 0.06, z)
      group.add(line)
    }
    scene.add(group)
    garages.push({ index: g, box: boxPoint, front, facing, board, back })
  }

  function setGarageLabel(index: number, name: string, number: number | string, color: string) {
    const garage = garages[index]
    if (!garage) return
    const material = garage.board.material as THREE.MeshBasicMaterial
    material.map?.dispose()
    material.map = bannerTexture(String(name).toUpperCase(), `#${number}`, '#0E0E0E', color, 512, 128)
    material.color.set(0xffffff)
    material.needsUpdate = true
    ;(garage.back.material as THREE.MeshLambertMaterial).color.set(color)
  }

  // ── Trackside: gravel, fences, boards, tyre walls, stands, gantries ──
  const trackside = addTrackside({
    scene, samples, width: W, centre, size, pitFrom, pitTo,
    pitNormal: laneNormal.clone().multiplyScalar(Math.sign(layout.pitOffset) || 1), pitReach: Math.abs(layout.pitOffset) + 12,
    gantries: layout.gantries, startU: layout.startU, straightStands: layout.straightStands,
  })
  let gantryTitle = { name: 'LAUNCHGP', tagline: 'start / finish' as string | null, color: '#FF5A1F' }
  let gantryBoards: { name: string; tagline: string | null; color: string }[] = []
  const refreshGantries = () =>
    trackside.setBanners([gantryTitle, ...gantryBoards], (name, tagline, color) => bannerTexture(name.toUpperCase(), tagline, color, ['#F3F3F1', '#FFD23F', '#2BD47D', '#00C2D1', '#FF7AC6', '#9AA0A6'].includes(color) ? '#0B0C0E' : '#F3F3F1', 1536, 192))

  // ── Footbridge with a sponsor banner (Endurance) and the start line ──
  const bannerMaterial = new THREE.MeshLambertMaterial({ map: bannerTexture('LAUNCHGP', 'start / finish', '#FF5A1F', '#0B0C0E', 1536, 192) })
  {
    const s = at(layout.bridgeU ?? layout.startU)
    const group = new THREE.Group()
    group.position.copy(s.p)
    group.rotation.y = Math.atan2(s.t.x, s.t.z)
    const span = W + (layout.bridgeU ? 14 : 6)
    const height = layout.bridgeU ? 7 : 9
    for (const sideX of [-1, 1]) {
      const post = new THREE.Mesh(new THREE.BoxGeometry(layout.bridgeU ? 3 : 0.6, height, layout.bridgeU ? 4 : 0.6), layout.bridgeU ? new THREE.MeshLambertMaterial({ color: 0x9a9a92 }) : steel)
      post.position.set(sideX * (span / 2), height / 2, 0)
      post.castShadow = true
      group.add(post)
    }
    if (!layout.bridgeU) group.visible = false // the Grand Prix uses the start-lights gantry
    const deck = new THREE.Mesh(new THREE.BoxGeometry(span + 2, layout.bridgeU ? 2.6 : 2.2, layout.bridgeU ? 5 : 0.5),
      [dark, dark, dark, dark, bannerMaterial, bannerMaterial])
    deck.position.set(0, height, 0)
    deck.castShadow = true
    group.add(deck)
    if (layout.bridgeU) {
      // Railings on the footbridge.
      for (const z of [-2.4, 2.4]) {
        const rail = new THREE.Mesh(new THREE.BoxGeometry(span + 2, 0.9, 0.15), steel)
        rail.position.set(0, height + 1.75, z)
        group.add(rail)
      }
    }
    scene.add(group)

    const line = at(layout.startU)
    const startLine = new THREE.Mesh(new THREE.PlaneGeometry(W, 2), new THREE.MeshLambertMaterial({ map: checkerTexture() }))
    startLine.rotation.x = -Math.PI / 2
    startLine.rotation.z = -Math.atan2(line.t.x, line.t.z)
    startLine.position.set(line.p.x, 0.09, line.p.z)
    scene.add(startLine)
  }
  function setBanner(title: string, sub: string) {
    bannerMaterial.map?.dispose()
    bannerMaterial.map = bannerTexture(title.toUpperCase(), sub, '#FF5A1F', '#0B0C0E', 1536, 192)
    bannerMaterial.needsUpdate = true
    gantryTitle = { name: title, tagline: sub, color: '#FF5A1F' }
    refreshGantries()
  }

  // ── Billboards around the outside ─────────────────────────────────────
  const boardMeshes: THREE.Mesh[] = []
  const boardCount = 8
  for (let k = 0; k < boardCount; k++) {
    const s = at(0.08 + k / boardCount)
    const dir = outside(s)
    const pos = s.p.clone().addScaledVector(s.n, dir * (W / 2 + 30))
    const group = new THREE.Group()
    group.position.copy(pos)
    group.lookAt(s.p.x, 0, s.p.z)
    const panel = new THREE.Mesh(new THREE.BoxGeometry(16, 4, 0.4), [dark, dark, dark, dark, new THREE.MeshLambertMaterial({ color: 0xf3f3f1 }), dark])
    panel.position.y = 4
    panel.castShadow = true
    group.add(panel)
    for (const x of [-6, 6]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.4, 2.2, 0.4), steel)
      leg.position.set(x, 1.1, -0.1)
      group.add(leg)
    }
    scene.add(group)
    boardMeshes.push(panel)
  }
  function setBillboards(boards: Billboard[]) {
    gantryBoards = boards.filter((b) => b.name !== 'YOUR BRAND HERE').map((b) => ({ name: b.name, tagline: b.tagline, color: b.color }))
    if (gantryBoards.length === 0) gantryBoards = [{ name: 'YOUR BRAND HERE', tagline: 'rent this banner', color: '#F3F3F1' }]
    refreshGantries()
    boardMeshes.forEach((panel, k) => {
      const b = boards.find((board) => board.spot === k) ?? { name: 'YOUR BRAND HERE', tagline: 'rent this billboard', color: '#F3F3F1' }
      const materials = panel.material as THREE.MeshLambertMaterial[]
      materials[4].map?.dispose()
      const fg = ['#F3F3F1', '#FFD23F', '#2BD47D', '#00C2D1', '#FF7AC6', '#9AA0A6'].includes(b.color) ? '#0B0C0E' : '#F3F3F1'
      materials[4].map = bannerTexture(b.name, b.tagline, b.color, fg)
      materials[4].color.set(0xffffff)
      materials[4].needsUpdate = true
    })
  }

  // ── Grandstands with crowd ────────────────────────────────────────────
  const crowdColors = [0xff5a1f, 0x4c9aff, 0xf3f3f1, 0xffd23f, 0x8a8f96, 0xe0321b].map((c) => new THREE.Color(c))
  for (const u of layout.stands) {
    const s = at(u)
    const dir = outside(s)
    const group = new THREE.Group()
    group.position.copy(s.p.clone().addScaledVector(s.n, dir * (W / 2 + 34)))
    group.lookAt(s.p.x, 0, s.p.z)
    const tierMat = new THREE.MeshLambertMaterial({ color: 0x3a3e45 })
    const perRow = 34
    const rows = 5
    const crowd = new THREE.InstancedMesh(new THREE.BoxGeometry(0.6, 0.9, 0.5), new THREE.MeshLambertMaterial(), perRow * rows)
    const m = new THREE.Matrix4()
    let k = 0
    for (let r = 0; r < rows; r++) {
      const tier = new THREE.Mesh(new THREE.BoxGeometry(36, 1, 2.4), tierMat)
      tier.position.set(0, r + 0.5, -r * 2.4)
      tier.receiveShadow = true
      group.add(tier)
      for (let c = 0; c < perRow; c++) {
        m.makeTranslation(-16.5 + c + (Math.random() - 0.5) * 0.3, r + 1.45, -r * 2.4)
        crowd.setMatrixAt(k, m)
        crowd.setColorAt(k, crowdColors[Math.floor(Math.random() * crowdColors.length)])
        k++
      }
    }
    group.add(crowd)
    const roof = new THREE.Mesh(new THREE.BoxGeometry(38, 0.4, 13), new THREE.MeshLambertMaterial({ color: 0xf3f3f1 }))
    roof.position.set(0, 8, -4.8)
    roof.castShadow = true
    group.add(roof)
    scene.add(group)
  }

  // ── Trees: round arcade trees scattered away from the track ────────────
  {
    const trunks = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.35, 0.5, 3, 6), new THREE.MeshLambertMaterial({ color: 0x6b4a2b }), 260)
    const crowns = new THREE.InstancedMesh(new THREE.IcosahedronGeometry(2.6, 1), new THREE.MeshLambertMaterial({ color: 0xffffff }), 260)
    const greens = [0x2f7d32, 0x3f9142, 0x25692b, 0x4da14a].map((c) => new THREE.Color(c))
    const probe = samples.filter((_, i) => i % 6 === 0).map((s) => s.p)
    const pit = [pitFrom, pitTo, pitFrom.clone().lerp(pitTo, 0.5)]
    const m = new THREE.Matrix4()
    const q = new THREE.Quaternion()
    let placed = 0
    let tries = 0
    const span = size * 0.75
    while (placed < 260 && tries < 6000) {
      tries++
      const x = centre.x + (Math.random() - 0.5) * span * 1.6
      const z = centre.z + (Math.random() - 0.5) * span * 1.6
      const p = new V3(x, 0, z)
      const clear = W / 2 + 40
      if (probe.some((t) => t.distanceToSquared(p) < clear * clear)) continue
      if (pit.some((t) => t.distanceToSquared(p) < 45 * 45)) continue
      const scale = 0.8 + Math.random() * 0.7
      m.compose(new V3(x, 1.5 * scale, z), q, new V3(scale, scale, scale))
      trunks.setMatrixAt(placed, m)
      m.compose(new V3(x, (3 + 2.2) * scale, z), q, new V3(scale, scale * 1.1, scale))
      crowns.setMatrixAt(placed, m)
      crowns.setColorAt(placed, greens[placed % greens.length])
      placed++
    }
    trunks.count = placed
    crowns.count = placed
    crowns.castShadow = true
    scene.add(trunks, crowns)
  }

  // ── Floodlights: lamp heads always, real lights on a few poles ────────
  const floodlights: THREE.PointLight[] = []
  const lamps: THREE.MeshBasicMaterial[] = []
  layout.lights.forEach((u, index) => {
    const s = at(u)
    const pos = s.p.clone().addScaledVector(s.n, outside(s) * (W / 2 + 27))
    const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.25, 0.35, 16, 8), steel)
    pole.position.set(pos.x, 8, pos.z)
    pole.castShadow = true
    scene.add(pole)
    const lampMaterial = new THREE.MeshBasicMaterial({ color: 0x8a8a80 })
    lamps.push(lampMaterial)
    const head = new THREE.Mesh(new THREE.BoxGeometry(2.4, 1, 0.6), lampMaterial)
    head.position.set(pos.x, 16.2, pos.z)
    scene.add(head)
    if (index % 2 === 0) {
      const light = new THREE.PointLight(0xfff1d6, 0, 90, 1.4)
      light.position.set(pos.x, 15, pos.z)
      scene.add(light)
      floodlights.push(light)
    }
  })

  return { curve, samples, pitCurve, pitEntryU, pitExitU, garages, floodlights, lamps, setGarageLabel, setBillboards, setBanner, size }
}
